import { INPUT_TEXT_MAX_LENGTH } from '@/common/constants';
import yup from '@/plugins/yup';
import { useField, useForm } from 'vee-validate';
import { projectPlanningService } from '../services/planning.service';
import { IClashTest } from '../interfaces';
import {
    showErrorNotificationFunction,
    showSuccessNotificationFunction,
} from '@/common/helpers';
import { useI18n } from 'vue-i18n';
import { projectPlanningModule } from '../store';
import {
    ClashTestMode,
    ComputeClashFor,
    NameUsedForClashPairs,
    ShowClashBy,
    UnitOfMeasurement,
} from '../constants';

const clashTestFormSchema = yup.object({
    name: yup.string().trim().max(INPUT_TEXT_MAX_LENGTH).required(),
    mode: yup.string().oneOf(Object.values(ClashTestMode)).required(),
    fromDate: yup.date().when('mode', {
        is: ClashTestMode.TIME_RANGE,
        then: yup.date().required(),
        otherwise: yup.date().notRequired().nullable(),
    }),
    toDate: yup.date().when('mode', {
        is: ClashTestMode.TIME_RANGE,
        then: yup.date().required().min(yup.ref('fromDate')),
        otherwise: yup.date().notRequired().nullable(),
    }),
    intervals: yup
        .number()
        .nullable()
        .when('mode', {
            is: ClashTestMode.TIME_RANGE,
            then: yup.number().required().integer().min(2),
            otherwise: yup.number().notRequired().nullable(),
        }),
    timeBaseTaskId: yup.string().notRequired().nullable(),
    computeCurve: yup.boolean().required(),
    computeVolume: yup.boolean().required(),
    computePenetrationDepth: yup.boolean().required(),
    threshold: yup.number().required().nullable(),
    displayIntersectionCurve: yup.boolean().required(),
    displayIntersectionVolume: yup.boolean().required(),
    displayPenetrationLine: yup.boolean().required(),
    clashIntersectionCurveColor: yup.string().required(),
    clashIntersectionVolumeColor: yup.string().required(),
    intersectionVolumeColor: yup.string().required(),
    penetrationLineColor: yup.string().required(),
    unit: yup.string().oneOf(Object.values(UnitOfMeasurement)).required(),
    visualizationMode: yup.string().oneOf(Object.values(ShowClashBy)).required(),
    zoomFit: yup.boolean().required(),
    zoomFactor: yup.number().required().nullable(),
    nodeTransparency: yup.number().required(),
    nameUsedForClashPairDisplay: yup
        .string()
        .oneOf(Object.values(NameUsedForClashPairs))
        .required(),
});

export const innitClashTestForm = {
    name: '',
    mode: ClashTestMode.FOCUS_TIME_SNAPSHOT,
    fromDate: undefined,
    toDate: undefined,
    intervals: null,
    timeBaseTaskId: null,
    computeCurve: false,
    computeVolume: false,
    computePenetrationDepth: true,
    threshold: 0.1,
    displayIntersectionCurve: true,
    displayIntersectionVolume: true,
    displayPenetrationLine: true,
    clashIntersectionCurveColor: '#ff8800',
    clashIntersectionVolumeColor: '#00ffff',
    intersectionVolumeColor: '#ffff00',
    penetrationLineColor: '#ff00ff',
    unit: UnitOfMeasurement.MILLIMETER,
    visualizationMode: ShowClashBy.GHOST_OTHER_PARTS,
    zoomFit: true,
    zoomFactor: 0.3,
    nodeTransparency: 50,
    nameUsedForClashPairDisplay: NameUsedForClashPairs.NODE,
};

export const setupClashTestForm = () => {
    const { t } = useI18n();

    const { errors, meta, handleSubmit, resetForm, setFieldValue } = useForm({
        initialValues: innitClashTestForm,
        validationSchema: clashTestFormSchema,
    });

    const onSubmit = async (selectedClashTest?: IClashTest | null) => {
        return await handleSubmit(async (values: any) => {
            let response;

            const payload = {
                ...values,
                name: values?.name?.trim(),
                nodeTransparency: values?.nodeTransparency
                    ? values?.nodeTransparency / 100
                    : 0,
                fromDate: values?.fromDate || null,
                toDate: values?.toDate || null,
                intervals:
                    values?.mode === ClashTestMode.TIME_RANGE
                        ? values?.intervals ?? null
                        : null,
                timeBaseTaskId:
                    values?.mode === ClashTestMode.TIME_RANGE
                        ? values?.timeBaseTaskId || null
                        : null,
            };

            if (selectedClashTest) {
                response = await projectPlanningService.updateClashTest(
                    selectedClashTest?._id || '',
                    payload,
                );
            } else {
                response = await projectPlanningService.createClashTest({
                    ...payload,
                    planningId: projectPlanningModule?.planning?._id || '',
                });
            }

            if (response?.success) {
                showSuccessNotificationFunction(
                    selectedClashTest
                        ? t('planning.clashTest.message.updateSuccess')
                        : t('planning.clashTest.message.createSuccess'),
                );
            } else {
                showErrorNotificationFunction(
                    response?.message ||
                        (selectedClashTest
                            ? t('planning.clashTest.message.updateError')
                            : t('planning.clashTest.message.createError')),
                );
            }

            return response;
        })();
    };

    const resetFormValues = (clashTest: IClashTest) => {
        const isTimeRange = clashTest?.mode === ClashTestMode.TIME_RANGE;

        resetForm({
            values: {
                name: clashTest?.name,
                mode: clashTest?.mode,
                fromDate: clashTest?.fromDate,
                toDate: clashTest?.toDate,
                intervals: isTimeRange ? clashTest?.intervals ?? 2 : null,
                timeBaseTaskId: isTimeRange
                    ? clashTest?.timeBaseTaskId ?? null
                    : null,
                computeCurve: !!clashTest?.computeCurve,
                computeVolume: !!clashTest?.computeVolume,
                computePenetrationDepth: !!clashTest?.computePenetrationDepth,
                threshold: clashTest?.threshold,
                displayIntersectionCurve: !!clashTest?.displayIntersectionCurve,
                displayIntersectionVolume: !!clashTest?.displayIntersectionVolume,
                displayPenetrationLine: !!clashTest?.displayPenetrationLine,
                clashIntersectionCurveColor: clashTest?.clashIntersectionCurveColor,
                clashIntersectionVolumeColor: clashTest?.clashIntersectionVolumeColor,
                intersectionVolumeColor: clashTest?.intersectionVolumeColor,
                penetrationLineColor: clashTest?.penetrationLineColor,
                unit: clashTest?.unit,
                visualizationMode: clashTest?.visualizationMode,
                zoomFit: !!clashTest?.zoomFit,
                zoomFactor: clashTest?.zoomFactor,
                nodeTransparency: clashTest?.nodeTransparency
                    ? Math.round(clashTest?.nodeTransparency * 100)
                    : 0,
                nameUsedForClashPairDisplay: clashTest?.nameUsedForClashPairDisplay,
            },
        });
    };

    const { value: name } = useField('name');
    const { value: mode } = useField('mode');
    const { value: fromDate } = useField('fromDate');
    const { value: toDate } = useField('toDate');
    const { value: intervals } = useField('intervals');
    const { value: timeBaseTaskId } = useField('timeBaseTaskId');
    const { value: computeCurve } = useField('computeCurve');
    const { value: computeVolume } = useField('computeVolume');
    const { value: computePenetrationDepth } = useField('computePenetrationDepth');
    const { value: threshold } = useField('threshold');
    const { value: displayIntersectionCurve } = useField('displayIntersectionCurve');
    const { value: displayIntersectionVolume } = useField('displayIntersectionVolume');
    const { value: displayPenetrationLine } = useField('displayPenetrationLine');
    const { value: clashIntersectionCurveColor } = useField(
        'clashIntersectionCurveColor',
    );
    const { value: clashIntersectionVolumeColor } = useField(
        'clashIntersectionVolumeColor',
    );
    const { value: intersectionVolumeColor } = useField('intersectionVolumeColor');
    const { value: penetrationLineColor } = useField('penetrationLineColor');
    const { value: unit } = useField('unit');
    const { value: visualizationMode } = useField('visualizationMode');
    const { value: zoomFit } = useField('zoomFit');
    const { value: zoomFactor } = useField('zoomFactor');
    const { value: nodeTransparency } = useField('nodeTransparency');
    const { value: nameUsedForClashPairDisplay } = useField(
        'nameUsedForClashPairDisplay',
    );

    return {
        name,
        mode,
        fromDate,
        toDate,
        intervals,
        timeBaseTaskId,
        computeCurve,
        computeVolume,
        computePenetrationDepth,
        threshold,
        displayIntersectionCurve,
        displayIntersectionVolume,
        displayPenetrationLine,
        clashIntersectionCurveColor,
        clashIntersectionVolumeColor,
        intersectionVolumeColor,
        penetrationLineColor,
        unit,
        visualizationMode,
        zoomFit,
        zoomFactor,
        nodeTransparency,
        nameUsedForClashPairDisplay,
        innitClashTestForm,
        meta,
        errors,
        onSubmit,
        resetFormValues,
        setFieldValue,
    };
};
